/**
 * Tier seed data – shared by SubscriptionTiersContext, AuthContext, App, and backend.
 * Kept in a separate file to avoid circular imports.
 *
 * APP_CONFIG_DEFAULTS: fallback when Firestore appConfiguration is empty.
 * Matches flattened structure from appConfiguration (app + contactForm + features + fileUpload + newUserDefaults).
 */
/** Draft limits for accounts with no paid entitlement (subscription map stays at 0 until Stripe / one-off credit). */
export const FREE_DRAFT_DEFAULTS = {
  maxCharacters: 30000,
  maxAgreementSizeKb: 100,
  maxSignatures: 2,
  maxEmailRecipients: 5,
  maxTemplates: 0,
  attachments: false,
  pdfExtract: false,
  logo: false,
  verification: false,
  templates: false,
}

/** Stricter draft after the one free send is used (still unpaid). */
export const FREE_DRAFT_AFTER_FREE_SEND_DEFAULTS = {
  maxCharacters: 25000,
  maxAgreementSizeKb: 80,
  maxSignatures: 2,
  maxEmailRecipients: 3,
  maxTemplates: 0,
  attachments: false,
  pdfExtract: false,
  logo: false,
  verification: false,
  templates: false,
}

export const APP_CONFIG_DEFAULTS = {
  draftExpiryMs: 24 * 60 * 60 * 1000,
  oneTimePrice: 6,
  maintenance: false,
  showAdminButton: true,
  PromoHeader: false,
  WelcomeEmail10Off: false,
  contactMessageLimit: 1000,
  contactAttachmentLimit: 5,
  contactAttachmentMaxBytes: 2 * 1024 * 1024,
  hideLogoFeature: true,
  hideVerificationFeature: true,
  maxCustomFields: 20,
  maxPdfBytes: 1048576,
  logoMaxBytes: 20480,
  logoMaxDimension: 120,
  maxAttachmentBytes: 8388608,
  freeDraft: FREE_DRAFT_DEFAULTS,
  freeDraftAfterFreeSend: FREE_DRAFT_AFTER_FREE_SEND_DEFAULTS,
  youtubeDemoVideo: '',
  carouselVideos: '',
  carouselVideoHeaders: '',
  showCarouselVideos: false,
  /** Homepage promo card (hero); off by default — enable in Firestore appConfiguration.app */
  promoMadnessEnabled: false,
  newUserDefaults: {
    subscription: {
      maxAttachments: 0,
      maxAttachmentBytes: 0,
      maxCharacters: 0,
      maxTemplates: 0,
      maxEmailsPerMonth: 0,
      maxEmailsSignedPerMonth: 0,
      maxSignedAgreementsPerMonth: 0,
      attachments: false,
      pdfExtract: false,
      templates: false,
      EmailEvents: false,
      logo: false,
      verification: false,
    },
    /** Reference for one-time paid sends (applied by Stripe webhook), not used at raw signup. */
    oneTime: {
      maxAttachments: 10,
      maxAttachmentBytes: 8388608,
      maxCharacters: 30000,
      maxAgreementSizeKb: 450,
      maxEmailRecipients: 10,
      maxTemplates: 25,
      maxEmailsPerMonth: 0,
      maxEmailsSignedPerMonth: 1,
      attachments: true,
      pdfExtract: true,
      templates: true,
      EmailEvents: true,
      logo: true,
      verification: true,
    },
  },
}

/** Basic, Standard, Premium + Premium One-Off. Top-ups removed (basicTopUp, standardTopUp, premiumTopUp). */
export const TIER_SEED_DATA = {
  basic: {
    annualDiscountPercent: 83,
    maxPdfBytes: 1048576,
    logoMaxBytes: 0,
    logoMaxDimension: 0,
    logo: false,
    maxSignatures: 2,
    verification: false,
    attachments: false,
    maxAttachments: 0,
    maxAttachmentBytes: 0,
    maxCharacters: 3000,
    maxAgreementSizeKb: 100,
    signatureTextLimitKB: 50,
    maxEmailRecipients: 5,
    maxTemplates: 3,
    maxEmailsPerMonth: 5,
    maxSignedAgreementsPerMonth: 5,
    maxEmailsSignedPerMonth: 5,
    maxEmailSignedSignaturesPerMonth: 5,
    pdfExtract: false,
    templates: false,
    EmailEvents: false,
    PrioritySupportEmail: false,
    emailReminder: true,
    Stripe: [
      { price: 12, type: 'monthly', priceId: 'price_1UNMXjJEz5nHKZSKuIMeS1gH' },
      { link: 'https://buy.stripe.com/bJe8wQ2mXgmJaP99l55J60q', price: 24, type: 'annual', priceId: 'price_1T8vdeJEz5nHKZSKT7MSHKnU' },
      { price: 5, type: 'monthly', priceId: 'price_1T8vddJEz5nHKZSKoUW9Y3CI', grandfathered: true },
    ],
    legacyStripe: [
      { link: 'https://buy.stripe.com/8x26oIe5FeeB4qL2WH5J60p', price: 5, type: 'monthly', priceId: 'price_1T8vddJEz5nHKZSKoUW9Y3CI' },
    ],
  },
  standard: {
    annualDiscountPercent: 69,
    maxPdfBytes: 1048576,
    logoMaxBytes: 20480,
    logoMaxDimension: 120,
    logo: true,
    maxSignatures: 2,
    verification: false,
    attachments: true,
    maxAttachments: 3,
    maxAttachmentBytes: 8388608,
    maxCharacters: 20000,
    maxAgreementSizeKb: 300,
    signatureTextLimitKB: 50,
    maxEmailRecipients: 10,
    maxTemplates: 10,
    maxEmailsPerMonth: 100,
    maxSignedAgreementsPerMonth: 100,
    maxEmailsSignedPerMonth: 100,
    maxEmailSignedSignaturesPerMonth: 5,
    pdfExtract: true,
    templates: true,
    EmailEvents: true,
    PrioritySupportEmail: false,
    emailReminder: true,
    Stripe: [
      { price: 18, type: 'monthly', priceId: 'price_1UNMCZJEz5nHKZSKdnBhlZUO' },
      { price: 67, type: 'annual', priceId: 'price_1UNMCZJEz5nHKZSKucjwyEvk' },
      { price: 8, type: 'monthly', priceId: 'price_1T8vdeJEz5nHKZSKQGIB33sS', grandfathered: true },
      { price: 35, type: 'annual', priceId: 'price_1T8vdfJEz5nHKZSK1dXys5jv', grandfathered: true },
    ],
    legacyStripe: [
      { link: 'https://buy.stripe.com/fZueVe9Pp3zX1ez40L5J60r', price: 8, type: 'monthly', priceId: 'price_1T8vdeJEz5nHKZSKQGIB33sS' },
      { link: 'https://buy.stripe.com/4gMdRa1iT2vT3mH7cX5J60s', price: 35, type: 'annual', priceId: 'price_1T8vdfJEz5nHKZSK1dXys5jv' },
    ],
  },
  premium: {
    annualDiscountPercent: 73,
    maxPdfBytes: 1048576,
    logoMaxBytes: 20480,
    logoMaxDimension: 120,
    logo: true,
    maxSignatures: 2,
    verification: true,
    attachments: true,
    maxAttachments: 10,
    maxAttachmentBytes: 8388608, // 8 MB
    maxCharacters: 30000,
    maxAgreementSizeKb: 450,
    signatureTextLimitKB: 50,
    maxEmailRecipients: 10,
    maxTemplates: 25,
    maxEmailsPerMonth: 300,
    maxSignedAgreementsPerMonth: 300,
    maxEmailsSignedPerMonth: 300,
    maxEmailSignedSignaturesPerMonth: 300,
    pdfExtract: true,
    templates: true,
    EmailEvents: true,
    PrioritySupportEmail: true,
    emailReminder: true,
    CurrentEmailsPerMonth: 0,
    CurrentEmailRecipients: 0,
    CurrentTotalTemplates: 0,
    CurrentTotalSignatures: 0,
    Stripe: [
      { price: 30, type: 'monthly', priceId: 'price_1UNMCZJEz5nHKZSKaqojnE14' },
      { price: 99, type: 'annual', priceId: 'price_1UNMCZJEz5nHKZSKvJzGvv4A' },
      { price: 12, type: 'monthly', priceId: 'price_1T8vdfJEz5nHKZSKxufvQLK5', grandfathered: true },
      { price: 49, type: 'annual', priceId: 'price_1T8vdgJEz5nHKZSKF9OOKLdz', grandfathered: true },
    ],
    legacyStripe: [
      { link: 'https://buy.stripe.com/eVq28sf9J5I59L58h15J60t', price: 12, type: 'monthly', priceId: 'price_1T8vdfJEz5nHKZSKxufvQLK5' },
      { link: 'https://buy.stripe.com/28E8wQaTtc6t0av9l55J60u', price: 49, type: 'annual', priceId: 'price_1T8vdgJEz5nHKZSKF9OOKLdz' },
    ],
    premiumOneOff: {
      link: 'https://buy.stripe.com/4gM8wQ9Pp2vT2iD8h15J60w',
      price: 6,
      priceId: 'price_1T8vhPJEz5nHKZSKXJgTRvzn',
      priceIds: ['price_1T8vhPJEz5nHKZSKXJgTRvzn'],
      productId: 'prod_U5g5tuZv58HYxP',
      name: 'One Off PDSU',
    },
  },
}
